/**
 * 네이버 스마트에디터2 활용을 위한 유틸리티 함수
 * 스크립트 로드, 에디터 인스턴스 생성, 콘텐츠 저장 등의 기능을 제공합니다.
 */

// 로딩 상태 관리 변수
let _isJQueryLoaded = false;
let _isSmartEditorLoaded = false;
let oEditors = []; // 에디터 인스턴스 배열

/**
 * jQuery가 로드되었는지 확인
 * @returns {boolean} jQuery 로드 여부
 */
const isJQueryLoaded = () => {
  return typeof window !== 'undefined' && typeof window.jQuery !== 'undefined';
};

/**
 * 스마트에디터가 로드되었는지 확인
 * @returns {boolean} 스마트에디터 로드 여부
 */
const isSmartEditorLoaded = () => {
  return typeof window !== 'undefined' && 
         typeof window.nhn !== 'undefined' && 
         typeof window.nhn.husky !== 'undefined' && 
         typeof window.nhn.husky.EZCreator !== 'undefined';
};

/**
 * 스크립트를 동적으로 로드
 * @param {string} url - 스크립트 URL
 * @param {Object} options - 옵션
 * @returns {Promise} 로드 완료 시 해결되는 Promise
 */
const loadScript = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    try {
      // 이미 로드된 스크립트인지 확인
      const existingScript = document.querySelector(`script[src="${url}"]`);
      if (existingScript) {
        resolve(true);
        return;
      }

      // 새 스크립트 요소 생성
      const script = document.createElement('script');
      script.src = url;
      script.async = options.async || false;
      script.charset = options.charset || 'utf-8';
      
      // 로드 이벤트 핸들러
      script.onload = () => {
        resolve(true);
      };
      
      // 오류 이벤트 핸들러
      script.onerror = (error) => {
        console.error(`스크립트 로드 실패: ${url}`, error);
        reject(new Error(`스크립트 로드 실패: ${url}`));
      };
      
      // 문서에 스크립트 추가
      document.head.appendChild(script);
    } catch (error) {
      console.error(`스크립트 로드 중 오류 발생: ${url}`, error);
      reject(error);
    }
  });
};

/**
 * 필요한 모든 스크립트 로드
 * @returns {Promise<boolean>} 모든 스크립트 로드 완료 시 true로 해결되는 Promise
 */
const loadAllScripts = async () => {
  try {
    // jQuery가 이미 로드되어 있는지 확인
    if (!isJQueryLoaded()) {
      // jQuery 로드
      await loadScript('https://code.jquery.com/jquery-1.12.4.min.js');
      _isJQueryLoaded = true;
    }

    // jQuery가 로드된 후에만 스마트에디터 로드
    if (_isJQueryLoaded && !isSmartEditorLoaded()) {
      // 스마트에디터 로드
      await loadScript('/smarteditor2/js/service/HuskyEZCreator.js');
      
      // 로드 확인
      let checkCount = 0;
      const maxChecks = 10;
      
      while (!isSmartEditorLoaded() && checkCount < maxChecks) {
        await new Promise(resolve => setTimeout(resolve, 200));
        checkCount++;
      }
      
      if (!isSmartEditorLoaded()) {
        throw new Error('스마트에디터 로드 실패: 타임아웃');
      }
      
      _isSmartEditorLoaded = true;
    } else if (isSmartEditorLoaded()) {
      _isSmartEditorLoaded = true;
    }
    
    return _isJQueryLoaded && _isSmartEditorLoaded;
  } catch (error) {
    console.error('스크립트 로드 실패:', error);
    return false;
  }
};

/**
 * 스마트에디터 인스턴스 생성
 * @param {string} editorId - 에디터 요소 ID
 * @returns {Promise<Object>} 생성된 에디터 인스턴스
 */
const createSmartEditorInstance = (editorId) => {
  return new Promise((resolve, reject) => {
    try {
      // 필수 조건 확인
      if (!isJQueryLoaded()) {
        reject(new Error('jQuery가 로드되지 않았습니다'));
        return;
      }
      
      if (!isSmartEditorLoaded()) {
        reject(new Error('스마트에디터가 로드되지 않았습니다'));
        return;
      }
      
      // textarea 요소 확인
      const textarea = document.getElementById(editorId);
      if (!textarea) {
        reject(new Error(`ID가 ${editorId}인 textarea를 찾을 수 없습니다`));
        return;
      }
      
      // 전역 oEditors 배열 초기화
      if (!window.oEditors) {
        window.oEditors = [];
      }
      
      // 기존에 같은 ID로 생성된 인스턴스가 있는지 확인
      if (window.oEditors.getById && window.oEditors.getById[editorId]) {
        destroyEditorInstance(editorId);
      }
      
      // 에디터 생성 타임아웃 설정
      const timeout = setTimeout(() => {
        reject(new Error('에디터 생성 시간 초과'));
      }, 10000); // 10초 타임아웃
      
      // 에디터 인스턴스 생성
      window.nhn.husky.EZCreator.createInIFrame({
        oAppRef: window.oEditors,
        elPlaceHolder: editorId,
        sSkinURI: '/smarteditor2/SmartEditor2Skin.html',
        fCreator: 'createSEditor2',
        htParams: {
          bUseToolbar: true,
          bUseVerticalResizer: true,
          bUseModeChanger: true,
          fOnBeforeUnload: function() {
            // 에디터 내용이 변경되었을 때 경고 메시지 표시 여부
            return false;
          }
        },
        fOnAppLoad: function() {
          // 에디터 로드 완료 시 호출
          clearTimeout(timeout);
          
          // 배열에 추가
          oEditors.push(editorId);
          
          // Promise 해결
          resolve(window.oEditors.getById[editorId]);
        }
      });
    } catch (error) {
      console.error('에디터 인스턴스 생성 중 오류:', error);
      reject(error);
    }
  });
};

/**
 * 에디터 내용 저장
 * @param {Object} editorInstance - 에디터 인스턴스
 * @param {string} editorId - 에디터 요소 ID
 * @returns {Promise<string>} 에디터 내용
 */
const saveEditorContent = (editorInstance, editorId) => {
  return new Promise((resolve, reject) => {
    try {
      // 필수 조건 확인
      if (!editorInstance) {
        reject(new Error('에디터 인스턴스가 없습니다'));
        return;
      }
      
      // textarea 요소 확인
      const textarea = document.getElementById(editorId);
      if (!textarea) {
        reject(new Error(`ID가 ${editorId}인 textarea를 찾을 수 없습니다`));
        return;
      }
      
      // 에디터 내용을 textarea에 업데이트 (필수!)
      editorInstance.exec('UPDATE_CONTENTS_FIELD', []);
      
      // 내용 가져오기
      const content = textarea.value;
      
      // Promise 해결
      resolve(content);
    } catch (error) {
      console.error('에디터 내용 저장 중 오류:', error);
      reject(error);
    }
  });
};

/**
 * 에디터 인스턴스 제거
 * @param {string} editorId - 에디터 요소 ID
 * @returns {boolean} 성공 여부
 */
const destroyEditorInstance = (editorId) => {
  try {
    // 전역 oEditors 확인
    if (!window.oEditors) {
      console.warn('oEditors가 초기화되지 않았습니다');
      return true;
    }
    
    // 인스턴스 제거 시도
    if (window.oEditors.getById && window.oEditors.getById[editorId]) {
      // 메모리에서 인스턴스 제거
      delete window.oEditors.getById[editorId];
      
      // 배열에서 ID 제거
      const index = oEditors.indexOf(editorId);
      if (index !== -1) {
        oEditors.splice(index, 1);
      }
      
    }
    
    return true;
  } catch (error) {
    console.error('에디터 인스턴스 제거 중 오류:', error);
    return false;
  }
};

export {
  loadAllScripts,
  createSmartEditorInstance,
  saveEditorContent,
  destroyEditorInstance
}; 